import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import {
  useCreatePlaybookRule,
  useCreatePlaybookRuleCategory,
  useDeletePlaybookRule,
  useDeletePlaybookRuleCategory,
  useUpdatePlaybookRuleCategory,
} from 'src/hooks';

import type { PlaybookDetail } from './types';

interface UsePlaybookRuleEditingOptions {
  onSelectRule: (ruleId: UUID | null) => void;
  playbook: PlaybookDetail;
  selectedRuleId: UUID | null;
}

/**
 * Every edit the rules list can make, and what each one does to the selection.
 *
 * There is no local draft state: adding a category or a rule persists it, so the
 * details panel opens onto a real rule it can edit by position id. That also
 * means deleting has to move the selection off what it removed — kept here with
 * the deletes rather than left to the list to remember.
 */
export function usePlaybookRuleEditing({
  onSelectRule,
  playbook,
  selectedRuleId,
}: UsePlaybookRuleEditingOptions) {
  const { createPlaybookRuleCategory } = useCreatePlaybookRuleCategory();
  const { updatePlaybookRuleCategory } = useUpdatePlaybookRuleCategory();
  const { deletePlaybookRuleCategory } = useDeletePlaybookRuleCategory();
  const { createPlaybookRule } = useCreatePlaybookRule();
  const { deletePlaybookRule } = useDeletePlaybookRule();

  const { categories } = playbook;

  /**
   * Adding a rule persists an empty one and selects it, so the details panel
   * opens onto a real rule the user fills in there. The panel edits positions by
   * id, which a purely local draft row has no way to provide.
   */
  const addRule = useCallback(
    async (categoryId: UUID): Promise<void> => {
      const { data } = await createPlaybookRule({
        categoryId,
        props: { positions: [{ aiGuidance: null, ruleText: '' }] },
      });
      if (data?.createPlaybookRule) onSelectRule(data.createPlaybookRule.id);
    },
    [createPlaybookRule, onSelectRule]
  );

  /**
   * Returns the new category's id so the list can focus its name. Created
   * without a name so the field shows its placeholder; names are free to
   * repeat within a playbook, so adding twice gives two unnamed categories.
   */
  const addCategory = useCallback(async (): Promise<UUID | null> => {
    const { data } = await createPlaybookRuleCategory({ name: '', playbookId: playbook.id });

    return data?.createPlaybookRuleCategory?.id ?? null;
  }, [createPlaybookRuleCategory, playbook.id]);

  const renameCategory = useCallback(
    async (categoryId: UUID, name: string): Promise<void> => {
      // An empty name is allowed: the field then shows its placeholder.
      const trimmed = name.trim();
      const category = categories.find(candidate => candidate.id === categoryId);
      if (!category || trimmed === category.name) return;

      await updatePlaybookRuleCategory({ categoryId, props: { name: trimmed } });
    },
    [categories, updatePlaybookRuleCategory]
  );

  const deleteCategory = useCallback(
    async (categoryId: UUID): Promise<void> => {
      const category = categories.find(candidate => candidate.id === categoryId);
      /**
       * A rule deleted with its category has to be deselected, or the details
       * panel renders a missing rule.
       */
      if (category?.rules.some(rule => rule.id === selectedRuleId)) onSelectRule(null);

      await deletePlaybookRuleCategory({ categoryId });
    },
    [categories, deletePlaybookRuleCategory, onSelectRule, selectedRuleId]
  );

  const deleteRule = useCallback(
    async (categoryId: UUID, ruleId: UUID): Promise<void> => {
      if (ruleId === selectedRuleId) onSelectRule(null);
      await deletePlaybookRule({ categoryId, ruleId });
    },
    [deletePlaybookRule, onSelectRule, selectedRuleId]
  );

  return { addCategory, addRule, deleteCategory, deleteRule, renameCategory };
}
